import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import WidgetLibrary from './components/WidgetLibrary';
import CanvasWorkspace from './components/CanvasWorkspace';
import PreviewPanel from './components/PreviewPanel';
import Icon from '../../components/AppIcon';

const PressKitBuilder = () => {
  const [widgets, setWidgets] = useState([]);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [pressKitData, setPressKitData] = useState({
    title: 'The Midnight Chronicles Press Kit',
    subtitle: 'A thrilling fantasy adventure by acclaimed author Emma Richardson',
    slug: 'midnight-chronicles-press-kit',
    status: 'draft',
    lastModified: new Date()
  });
  const [isSaving, setIsSaving] = useState(false);

  // Mock existing widgets for demonstration
  useEffect(() => {
    const mockWidgets = [
    {
      id: 'widget-demo-1',
      type: 'author-bio',
      name: 'Author Bio Card',
      position: { x: 40, y: 40 },
      size: { width: 320, height: 180 },
      content: {
        name: 'Emma Richardson',
        bio: `Emma Richardson is a bestselling fantasy author with over 15 years of experience crafting immersive worlds and compelling characters. Her previous works have been translated into 12 languages and have sold over 2 million copies worldwide.\n\nShe holds an MFA in Creative Writing from Columbia University and currently resides in Portland, Oregon, where she draws inspiration from the Pacific Northwest's mystical landscapes.`, image: "https://images.unsplash.com/photo-1628595556262-4cffd053a4bf", imageAlt: 'Professional portrait of Emma Richardson, fantasy author with warm smile and confident expression in modern office setting'
      },
      style: {
        backgroundColor: '#ffffff', borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        padding: 16
      }
    },
    {
      id: 'widget-demo-2', type: 'book-cover', name: 'Book Cover',
      position: { x: 400, y: 40 },
      size: { width: 240, height: 320 },
      content: {
        image: "https://images.unsplash.com/photo-1707241122343-ee097ed29c02", imageAlt: 'The Midnight Chronicles book cover featuring mystical forest scene with glowing moon and ethereal fantasy elements', title: 'The Midnight Chronicles', author: 'Emma Richardson'
      },
      style: {
        backgroundColor: '#ffffff', borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        padding: 0
      }
    }];

    setWidgets(mockWidgets);
  }, []);

  const handleWidgetAdd = (widget) => {
    setWidgets((prev) => [...prev, widget]);
    setSelectedWidget(widget);
  };

  const handleWidgetUpdate = (widgetId, updates) => {
    setWidgets((prev) => prev?.map((widget) =>
    widget?.id === widgetId ?
    { ...widget, ...updates } :
    widget
    ));

    // Update selected widget if it's the one being updated
    if (selectedWidget?.id === widgetId) {
      setSelectedWidget((prev) => ({ ...prev, ...updates }));
    }
  };

  const handleWidgetDelete = (widgetId) => {
    setWidgets((prev) => prev?.filter((widget) => widget?.id !== widgetId));
    if (selectedWidget?.id === widgetId) {
      setSelectedWidget(null);
    }
  };

  const handleWidgetSelect = (widget) => {
    setSelectedWidget(widget);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Simulate save operation
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setPressKitData((prev) => ({
        ...prev,
        lastModified: new Date()
      }));
      console.log('Press kit saved successfully');
    } catch (error) {
      console.error('Error saving press kit:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async (shareableLink) => {
    try {
      setPressKitData((prev) => ({
        ...prev,
        status: 'published',
        publishedAt: new Date(),
        shareableLink
      }));
      console.log('Press kit published successfully:', shareableLink);
    } catch (error) {
      console.error('Error publishing press kit:', error);
    }
  };

  const handleGenerateLink = (previewLink) => {
    console.log('Preview link generated:', previewLink);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Press Kit Builder - PenGame PR Hub</title>
        <meta name="description" content="Create professional, interactive press kits with drag-and-drop functionality and customizable widgets for authors and publishers." />
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="px-6 py-6">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Press Kit Builder</h1>
              <p className="text-muted-foreground">
                Create professional press kits with drag-and-drop widgets and live preview
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Status Indicator */}
              <div className="flex items-center space-x-2 px-3 py-2 bg-muted rounded-lg">
                <div className={`w-2 h-2 rounded-full ${
                pressKitData?.status === 'published' ? 'bg-success' : 'bg-warning'}`
                } />
                <span className="text-sm font-medium text-foreground capitalize">
                  {pressKitData?.status}
                </span>
              </div>

              {/* Quick Actions */}
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center space-x-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors duration-200 disabled:opacity-50">

                <Icon name={isSaving ? "Loader2" : "Save"} size={16} className={isSaving ? "animate-spin" : ""} />
                <span className="text-sm font-medium">
                  {isSaving ? 'Saving...' : 'Save'}
                </span>
              </button>

              <button className="flex items-center space-x-2 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted transition-colors duration-200">
                <Icon name="Settings" size={16} />
                <span className="text-sm font-medium">Settings</span>
              </button>
            </div>
          </div>

          {/* Press Kit Info */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-1">
                  {pressKitData?.title}
                </h2>
                <p className="text-sm text-muted-foreground mb-2">
                  {pressKitData?.subtitle}
                </p>
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <span>Last modified: {pressKitData?.lastModified?.toLocaleDateString()}</span>
                  <span>•</span>
                  <span>{widgets?.length} widgets</span>
                  {pressKitData?.shareableLink &&
                  <>
                      <span>•</span>
                      <a
                      href={pressKitData?.shareableLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 flex items-center space-x-1">

                        <Icon name="ExternalLink" size={12} />
                        <span>View Live</span>
                      </a>
                    </>
                  }
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button className="p-2 rounded-md bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors duration-200">
                  <Icon name="Edit2" size={16} />
                </button>
                <button className="p-2 rounded-md bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors duration-200">
                  <Icon name="Copy" size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Builder Interface */}
        <div className="flex h-[calc(100vh-280px)]">
          {/* Widget Library */}
          <WidgetLibrary onWidgetDrag={handleWidgetSelect} />
          
          {/* Canvas Workspace */}
          <CanvasWorkspace
            widgets={widgets}
            onWidgetAdd={handleWidgetAdd}
            onWidgetUpdate={handleWidgetUpdate}
            onWidgetDelete={handleWidgetDelete}
            onWidgetSelect={handleWidgetSelect}
            selectedWidget={selectedWidget} />

          
          {/* Preview Panel */}
          <PreviewPanel
            widgets={widgets}
            pressKitData={pressKitData}
            onSave={handleSave}
            onPublish={handlePublish}
            onGenerateLink={handleGenerateLink} />

        </div>

        {/* Help Section */}
        <div className="px-6 py-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="HelpCircle" size={16} />
                <span>Need help? Check out our</span>
                <button className="text-primary hover:text-primary/80 font-medium">
                  Press Kit Guide
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="Keyboard" size={16} />
                <span>Keyboard shortcuts available</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Zap" size={16} />
                <span>Auto-save enabled</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default PressKitBuilder;